import React, {
	FC,
	ReactNode,
	useState,
	useRef,
	useEffect,
	useCallback,
} from 'react';
import { useAnimation, useMotionValue } from 'framer-motion';
import { StyledTrackWrapper, StyledTrack } from './styles';

export interface TrackPropTypes {
	setTrackIsActive: any;
	trackIsActive: boolean;
	setActiveItem: any;
	activeItem: any;
	positions: any;
	children: ReactNode;
	gap: number;
}

const Track: FC<TrackPropTypes> = ({
	setTrackIsActive,
	trackIsActive,
	setActiveItem,
	activeItem,
	positions,
	children,
	gap,
}) => {
	const [dragStartPosition, setDragStartPosition] = useState(0);
	const controls = useAnimation();
	const x = useMotionValue(0);
	const node = useRef<any>(null);

	const handleDragStart = () => setDragStartPosition(positions[activeItem]);

	const transitionProps = {
		stiffness: 400,
		type: 'spring',
		damping: 60,
		mass: 3,
	};

	const handleDragEnd = (_: any, info: any) => {
		const distance = info.offset.x;
		const velocity = info.velocity.x;
		const direction = velocity < 0 || distance < 0 ? 1 : -1;

		const extrapolatedPosition =
			dragStartPosition +
			(direction === 1
				? Math.min(velocity, distance)
				: Math.max(velocity, distance));

		const closestPosition = positions.reduce(
			(prev: number, curr: number) => {
				if (
					Math.abs(curr - extrapolatedPosition) <
					Math.abs(prev - extrapolatedPosition)
				) {
					return curr;
				}
				return prev;
			},
			0,
		);

		if (!(closestPosition < positions[positions.length])) {
			setActiveItem(positions.indexOf(closestPosition));
			controls.start({
				x: closestPosition,
				transition: {
					velocity: info.velocity.x,
					...transitionProps,
				},
			});
		} else {
			setActiveItem(positions.length);
			controls.start({
				x: positions[positions.length],
				transition: {
					velocity: info.velocity.x,
					...transitionProps,
				},
			});
		}
	};

	const handleResize = useCallback(
		() =>
			controls.start({
				x: positions[activeItem],
				transition: {
					...transitionProps,
				},
			}),
		[activeItem, controls, positions],
	);

	const handleClick = useCallback(
		(event: any) =>
			node?.current?.contains(event.target)
				? setTrackIsActive(true)
				: setTrackIsActive(false),
		[setTrackIsActive],
	);

	const handleKeyDown = useCallback(
		(event: any) => {
			if (trackIsActive) {
				if (activeItem < positions.length) {
					if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
						event.preventDefault();
						setActiveItem((prev: number) => prev + 1);
					}
				}
				if (activeItem > positions.length - positions.length) {
					if (
						event.key === 'ArrowLeft' ||
						event.key === 'ArrowDown'
					) {
						event.preventDefault();
						setActiveItem((prev: number) => prev - 1);
					}
				}
			}
		},
		[trackIsActive, setActiveItem, activeItem, positions.length],
	);

	useEffect(() => {
		handleResize();

		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('mousedown', handleClick);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('mousedown', handleClick);
		};
	}, [handleClick, handleResize, handleKeyDown, positions]);

	return (
		<StyledTrackWrapper ref={node}>
			<StyledTrack
				dragConstraints={node}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				animate={controls}
				style={{
					x,
				}}
				drag="x"
				$gap={gap}
			>
				{children}
			</StyledTrack>
		</StyledTrackWrapper>
	);
};

export default Track;
